import { createContext, useEffect, useState, ReactNode } from 'react';
import { ThemeContextType, BrandingConfig } from '@/types';
import { apiClient } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { devLog } from '@/utils/logger';
import { generateColorPalette } from '@/utils/colorPalette';
import { getContrastColor } from '@/utils/colors';

/**
 * Theme Context
 *
 * Manages dynamic branding and theme configuration:
 * - Fetches branding from API (team → agency → defaults)
 * - Injects CSS variables for colors, fonts, logo
 * - Provides branding data to components
 *
 * Cascade Priority:
 * 1. Team branding (highest priority)
 * 2. Agency branding
 * 3. Default values (from theme-variables.css)
 *
 * SECURITY NOTE:
 * - Branding is only fetched when user is authenticated
 * - On public pages (login, register, forgot-password), uses default branding
 * - This prevents 404 errors on /api/branding endpoint
 */

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

// Default branding configuration
const DEFAULT_BRANDING: BrandingConfig = {
  logo: '/images/logo.png',
  logoSmall: '/images/aa-logo-simple.png',
  colors: {
    primary: '#69C7C1',
    secondary: '#074F47',
    coral: '#ff5757',
    softCoral: '#FBD3D1',
    softMint: '#D5EEEB',
    peach: '#FBCCAE',
    lightTealGray: '#B9C7C7',
    sageGray: '#718080',
    warmGray: '#DAD2D1',
    offWhite: '#F6F8F5',
  },
  fontFamily: 'DM Sans, sans-serif',
  isWhitelabelEnabled: false,
};

/**
 * CSS default values for --bs-* variables (from index.css)
 * Used to detect when a whitelabel value actually differs from the CSS defaults,
 * since the backend always returns all colors (filling in its own defaults which
 * may differ from the CSS defaults).
 */
const CSS_DEFAULTS: Record<string, string> = {
  gray: '#6c757d',
  gray2: '#DDE2E5',
  gray400: '#ced4da',
  gray600: '#6c757d',
  grayStone: '#54595f',
  light: '#f0f2f5',
};

/**
 * Validate hex color format to prevent CSS injection
 * Accepts: #RGB, #RGBA, #RRGGBB, #RRGGBBAA
 */
const isValidHexColor = (color: string | undefined): boolean => {
  if (!color) return false;
  return /^#[0-9A-Fa-f]{3,8}$/.test(color);
};

/**
 * Validate RGBA color format
 * Accepts: rgba(r, g, b, a) format
 */
const isValidRgbaColor = (color: string | undefined): boolean => {
  if (!color) return false;
  return /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*[\d.]+\s*)?\)$/.test(color);
};

/**
 * Validate color (hex or rgba)
 */
const isValidColor = (color: string | undefined): boolean => {
  return isValidHexColor(color) || isValidRgbaColor(color);
};

/**
 * Validate URL to prevent injection (only https and relative paths)
 */
const isValidAssetUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  // Allow relative paths starting with /
  if (url.startsWith('/')) return true;
  // Allow https URLs only
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Validate Google Fonts URL specifically
 */
const isValidGoogleFontsUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' &&
           (parsed.hostname === 'fonts.googleapis.com' ||
            parsed.hostname === 'fonts.gstatic.com');
  } catch {
    return false;
  }
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Dynamically load Google Fonts
   * Removes existing custom font link and adds new one
   */
  const loadGoogleFont = (fontUrl: string): void => {
    // Validate Google Fonts URL
    if (!isValidGoogleFontsUrl(fontUrl)) {
      devLog.warn('[ThemeContext] Invalid Google Fonts URL, skipping font load');
      return;
    }

    // Remove existing custom font link if any
    const existingLink = document.getElementById('custom-google-font');
    if (existingLink) {
      existingLink.remove();
    }

    // Add new font link
    const link = document.createElement('link');
    link.id = 'custom-google-font';
    link.rel = 'stylesheet';
    link.href = fontUrl;
    document.head.appendChild(link);
  };

  /**
   * Inject CSS variables into :root
   * This allows Tailwind and custom CSS to use dynamic theme values
   * Injects all 11 color variables from the branding configuration
   * Generates full shade palettes (25, 50, 100-900) for primary/secondary
   * Syncs legacy --bs-* variables for backward compatibility
   */
  // Shades that carry a per-shade foreground contrast token (text-{family}-{shade}-contrast)
  const CONTRAST_SHADES: readonly string[] = ['400', '500', '600', '700', '800'];

  const setShadeContrastVars = (
    root: HTMLElement,
    fam: 'primary' | 'secondary' | 'coral',
    palette: Record<string, string>,
  ) => {
    for (const shade of CONTRAST_SHADES) {
      const color = palette[shade];
      if (color) root.style.setProperty(`--color-${fam}-${shade}-contrast`, getContrastColor(color));
    }
  };

  const injectCSSVariables = (config: BrandingConfig) => {
    const root = document.documentElement;

    // Clear all previously injected shade variables so CSS defaults take over when resetting
    const shadeKeys = ['25', '50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];
    for (const prefix of ['--color-primary-', '--color-secondary-', '--color-coral-']) {
      for (const shade of shadeKeys) {
        root.style.removeProperty(`${prefix}${shade}`);
      }
    }
    // Clear contrast overrides (Tailwind fallback #ffffff takes over on default)
    for (const fam of ['primary', 'secondary', 'coral']) {
      root.style.removeProperty(`--color-${fam}-contrast`);
      for (const shade of CONTRAST_SHADES) {
        root.style.removeProperty(`--color-${fam}-${shade}-contrast`);
      }
    }
    // Clear legacy overrides
    root.style.removeProperty('--bs-primary');
    root.style.removeProperty('--bs-secondary');

    // Primary color
    if (config.colors.primary && isValidColor(config.colors.primary)) {
      const isCustomized = config.isWhitelabelEnabled &&
        config.colors.primary.toLowerCase() !== DEFAULT_BRANDING.colors.primary.toLowerCase();
      if (isCustomized) {
        // Custom whitelabel color: generate full shade palette + sync legacy variable
        const palette = generateColorPalette(config.colors.primary);
        Object.entries(palette).forEach(([shade, color]) => {
          root.style.setProperty(`--color-primary-${shade}`, color);
        });
        root.style.setProperty('--bs-primary', config.colors.primary);
        root.style.setProperty('--color-primary-contrast', getContrastColor(config.colors.primary));
        setShadeContrastVars(root, 'primary', palette);
      } else {
        // Default or non-customized: only set 500 shade, keep hand-curated shades
        root.style.setProperty('--color-primary-500', config.colors.primary);
      }
    }

    // Secondary color
    if (config.colors.secondary && isValidColor(config.colors.secondary)) {
      const isCustomized = config.isWhitelabelEnabled &&
        config.colors.secondary.toLowerCase() !== DEFAULT_BRANDING.colors.secondary.toLowerCase();
      if (isCustomized) {
        // Custom whitelabel color: generate full shade palette + sync legacy variable
        const palette = generateColorPalette(config.colors.secondary);
        Object.entries(palette).forEach(([shade, color]) => {
          root.style.setProperty(`--color-secondary-${shade}`, color);
        });
        root.style.setProperty('--bs-secondary', config.colors.secondary);
        root.style.setProperty('--color-secondary-contrast', getContrastColor(config.colors.secondary));
        setShadeContrastVars(root, 'secondary', palette);
      } else {
        // Default or non-customized: only set 500 shade, keep hand-curated shades
        root.style.setProperty('--color-secondary-500', config.colors.secondary);
      }
    }

    if (config.colors.secondaryRgba && isValidColor(config.colors.secondaryRgba)) {
      root.style.setProperty('--color-secondary-rgba', config.colors.secondaryRgba);
    }

    // Gray and utility color overrides
    // Only sync --bs-* legacy variables when whitelabel is enabled AND
    // the value actually differs from the CSS defaults (backend defaults ≠ CSS defaults)
    if (config.colors.gray && isValidColor(config.colors.gray)) {
      root.style.setProperty('--color-gray-500', config.colors.gray);
      if (config.isWhitelabelEnabled && config.colors.gray.toLowerCase() !== CSS_DEFAULTS.gray.toLowerCase())
        root.style.setProperty('--bs-gray', config.colors.gray);
    }
    if (config.colors.gray2 && isValidColor(config.colors.gray2)) {
      root.style.setProperty('--color-gray-200', config.colors.gray2);
      if (config.isWhitelabelEnabled && config.colors.gray2.toLowerCase() !== CSS_DEFAULTS.gray2.toLowerCase())
        root.style.setProperty('--bs-gray-2', config.colors.gray2);
    }
    if (config.colors.gray400 && isValidColor(config.colors.gray400)) {
      root.style.setProperty('--color-gray-400', config.colors.gray400);
      if (config.isWhitelabelEnabled && config.colors.gray400.toLowerCase() !== CSS_DEFAULTS.gray400.toLowerCase())
        root.style.setProperty('--bs-gray-400', config.colors.gray400);
    }
    if (config.colors.gray600 && isValidColor(config.colors.gray600)) {
      root.style.setProperty('--color-gray-600', config.colors.gray600);
      if (config.isWhitelabelEnabled && config.colors.gray600.toLowerCase() !== CSS_DEFAULTS.gray600.toLowerCase())
        root.style.setProperty('--bs-gray-600', config.colors.gray600);
    }
    if (config.colors.grayStone && isValidColor(config.colors.grayStone)) {
      root.style.setProperty('--color-gray-stone', config.colors.grayStone);
      if (config.isWhitelabelEnabled && config.colors.grayStone.toLowerCase() !== CSS_DEFAULTS.grayStone.toLowerCase())
        root.style.setProperty('--bs-gray-stone', config.colors.grayStone);
    }
    if (config.colors.light && isValidColor(config.colors.light)) {
      root.style.setProperty('--color-light', config.colors.light);
      if (config.isWhitelabelEnabled && config.colors.light.toLowerCase() !== CSS_DEFAULTS.light.toLowerCase())
        root.style.setProperty('--bs-light', config.colors.light);
    }

    // Calendar highlight and black button (no legacy counterparts)
    if (config.colors.calendarHighlight && isValidColor(config.colors.calendarHighlight)) {
      root.style.setProperty('--color-calendar-highlight', config.colors.calendarHighlight);
    }
    if (config.colors.blackButton && isValidColor(config.colors.blackButton)) {
      root.style.setProperty('--color-black-button', config.colors.blackButton);
    }

    // Coral accent color
    if (config.colors.coral && isValidColor(config.colors.coral)) {
      const isCustomized = config.isWhitelabelEnabled &&
        config.colors.coral.toLowerCase() !== DEFAULT_BRANDING.colors.coral!.toLowerCase();
      if (isCustomized) {
        const palette = generateColorPalette(config.colors.coral);
        Object.entries(palette).forEach(([shade, color]) => {
          root.style.setProperty(`--color-coral-${shade}`, color);
        });
        root.style.setProperty('--color-coral-contrast', getContrastColor(config.colors.coral));
        setShadeContrastVars(root, 'coral', palette);
      } else {
        root.style.setProperty('--color-coral-500', config.colors.coral);
      }
    }

    // Soft coral background
    if (config.colors.softCoral && isValidColor(config.colors.softCoral)) {
      root.style.setProperty('--color-soft-coral', config.colors.softCoral);
    }

    // Brand neutral colors
    if (config.colors.softMint && isValidColor(config.colors.softMint)) {
      root.style.setProperty('--color-soft-mint', config.colors.softMint);
    }
    if (config.colors.peach && isValidColor(config.colors.peach)) {
      root.style.setProperty('--color-peach', config.colors.peach);
    }
    if (config.colors.lightTealGray && isValidColor(config.colors.lightTealGray)) {
      root.style.setProperty('--color-light-teal-gray', config.colors.lightTealGray);
    }
    if (config.colors.sageGray && isValidColor(config.colors.sageGray)) {
      root.style.setProperty('--color-sage-gray', config.colors.sageGray);
    }
    if (config.colors.warmGray && isValidColor(config.colors.warmGray)) {
      root.style.setProperty('--color-warm-gray', config.colors.warmGray);
    }
    if (config.colors.offWhite && isValidColor(config.colors.offWhite)) {
      root.style.setProperty('--color-off-white', config.colors.offWhite);
    }

    // Inject font family (always set, fall back to default)
    root.style.setProperty(
      '--font-family-primary',
      config.fontFamily || DEFAULT_BRANDING.fontFamily || "'DM Sans', sans-serif"
    );

    // Load Google Font if URL provided
    if (config.fontUrl) {
      loadGoogleFont(config.fontUrl);
    }

    // Inject headline/display font family
    if (config.headlineFontFamily) {
      root.style.setProperty('--font-family-display', config.headlineFontFamily);

      // Load Google Font for headline if URL provided
      if (config.headlineFontUrl) {
        const existingHeadlineLink = document.getElementById('custom-headline-font');
        if (existingHeadlineLink) {
          existingHeadlineLink.remove();
        }

        if (isValidGoogleFontsUrl(config.headlineFontUrl)) {
          const link = document.createElement('link');
          link.id = 'custom-headline-font';
          link.rel = 'stylesheet';
          link.href = config.headlineFontUrl;
          document.head.appendChild(link);
        }
      }
    } else {
      // Reset to default Wagner font and remove any loaded custom headline font
      root.style.setProperty('--font-family-display', "'Wagner', sans-serif");
      const existingHeadlineLink = document.getElementById('custom-headline-font');
      if (existingHeadlineLink) {
        existingHeadlineLink.remove();
      }
    }

    // Inject logo URLs (with validation to prevent CSS injection)
    if (config.logo && isValidAssetUrl(config.logo)) {
      root.style.setProperty('--logo-url', `url('${config.logo}')`);
    }
    if (config.logoSmall && isValidAssetUrl(config.logoSmall)) {
      root.style.setProperty('--logo-small-url', `url('${config.logoSmall}')`);
    }
  };


  /**
   * Initialize branding when authentication is ready
   *
   * Flow:
   * 1. Wait for auth to finish loading
   * 2. If authenticated, fetch branding from API
   * 3. If not authenticated (public pages), use defaults
   * 4. Re-fetch when user changes team (currentTeamId)
   * 5. AbortController cancels stale requests when team changes
   */
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      devLog.log('[ThemeProvider] Waiting for auth to initialize...');
      return;
    }

    // Only fetch branding if user is authenticated
    if (!isAuthenticated) {
      devLog.log('[ThemeProvider] Not authenticated, using default branding');
      setBranding(DEFAULT_BRANDING);
      injectCSSVariables(DEFAULT_BRANDING);
      return;
    }

    // Create abort controller for this fetch
    const controller = new AbortController();

    // User is authenticated, fetch team branding
    devLog.log('[ThemeProvider] User authenticated, fetching branding...');

    const loadBranding = async () => {
      setIsLoading(true);

      try {
        const response = await apiClient.get('/api/branding', {
          signal: controller.signal,
        });
        const resolvedBranding = response.data.data; // Extract from { success: true, data: {...} }

        // Map backend ResolvedBranding to frontend BrandingConfig
        const effectiveBranding: BrandingConfig = {
          logo: resolvedBranding.assets?.logoBig || resolvedBranding.assets?.logoSmall || DEFAULT_BRANDING.logo,
          logoSmall: resolvedBranding.assets?.logoSmall || DEFAULT_BRANDING.logoSmall,
          colors: {
            primary: resolvedBranding.colors?.primary || DEFAULT_BRANDING.colors.primary,
            secondary: resolvedBranding.colors?.secondary || DEFAULT_BRANDING.colors.secondary,
            secondaryRgba: resolvedBranding.colors?.secondaryRgba,
            gray: resolvedBranding.colors?.gray,
            gray2: resolvedBranding.colors?.gray2,
            gray400: resolvedBranding.colors?.gray400,
            gray600: resolvedBranding.colors?.gray600,
            grayStone: resolvedBranding.colors?.grayStone,
            light: resolvedBranding.colors?.light,
            calendarHighlight: resolvedBranding.colors?.calendarHighlight,
            blackButton: resolvedBranding.colors?.blackButton,
            coral: resolvedBranding.colors?.coral || DEFAULT_BRANDING.colors.coral,
            softCoral: resolvedBranding.colors?.softCoral || DEFAULT_BRANDING.colors.softCoral,
            softMint: resolvedBranding.colors?.softMint || DEFAULT_BRANDING.colors.softMint,
            peach: resolvedBranding.colors?.peach || DEFAULT_BRANDING.colors.peach,
            lightTealGray: resolvedBranding.colors?.lightTealGray || DEFAULT_BRANDING.colors.lightTealGray,
            sageGray: resolvedBranding.colors?.sageGray || DEFAULT_BRANDING.colors.sageGray,
            warmGray: resolvedBranding.colors?.warmGray || DEFAULT_BRANDING.colors.warmGray,
            offWhite: resolvedBranding.colors?.offWhite || DEFAULT_BRANDING.colors.offWhite,
          },
          fontFamily: resolvedBranding.font?.family || DEFAULT_BRANDING.fontFamily,
          fontUrl: resolvedBranding.font?.url,
          headlineFontFamily: resolvedBranding.headlineFont?.family,
          headlineFontUrl: resolvedBranding.headlineFont?.url,
          assets: resolvedBranding.assets,
          isWhitelabelEnabled: resolvedBranding.isWhitelabelEnabled || false,
        };

        setBranding(effectiveBranding);
        injectCSSVariables(effectiveBranding);
      } catch (error: unknown) {
        // Ignore aborted requests (expected when team changes)
        const err = error as { name?: string; code?: string };
        if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          devLog.log('[ThemeProvider] Fetch aborted (team changed)');
          return;
        }

        // Failed to fetch branding - use defaults
        devLog.warn('[ThemeProvider] Failed to fetch branding, using defaults:', error);
        setBranding(DEFAULT_BRANDING);
        injectCSSVariables(DEFAULT_BRANDING);
      } finally {
        setIsLoading(false);
      }
    };

    loadBranding();

    // Cleanup: abort in-flight request when team changes
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, user?.currentTeamId, user?.teamId]); // Re-fetch when auth state or team changes

  /**
   * Cleanup: Remove custom font link when component unmounts
   */
  useEffect(() => {
    return () => {
      const existingLink = document.getElementById('custom-google-font');
      if (existingLink) {
        existingLink.remove();
      }
      const existingHeadlineLink = document.getElementById('custom-headline-font');
      if (existingHeadlineLink) {
        existingHeadlineLink.remove();
      }
    };
  }, []);

  /**
   * Public API for refreshing branding
   * (e.g., after user switches team or updates branding settings)
   */
  const refreshBranding = async (): Promise<void> => {
    // Don't set isLoading — this is a background refresh, not initial load.
    // Setting loading state here triggers an app-wide re-render that can block navigation.
    try {
      const response = await apiClient.get('/api/branding');
      const resolvedBranding = response.data.data;

      const effectiveBranding: BrandingConfig = {
        logo: resolvedBranding.assets?.logoBig || resolvedBranding.assets?.logoSmall || DEFAULT_BRANDING.logo,
        logoSmall: resolvedBranding.assets?.logoSmall || DEFAULT_BRANDING.logoSmall,
        colors: {
          primary: resolvedBranding.colors?.primary || DEFAULT_BRANDING.colors.primary,
          secondary: resolvedBranding.colors?.secondary || DEFAULT_BRANDING.colors.secondary,
          secondaryRgba: resolvedBranding.colors?.secondaryRgba,
          gray: resolvedBranding.colors?.gray,
          gray2: resolvedBranding.colors?.gray2,
          gray400: resolvedBranding.colors?.gray400,
          gray600: resolvedBranding.colors?.gray600,
          grayStone: resolvedBranding.colors?.grayStone,
          light: resolvedBranding.colors?.light,
          calendarHighlight: resolvedBranding.colors?.calendarHighlight,
          blackButton: resolvedBranding.colors?.blackButton,
          coral: resolvedBranding.colors?.coral || DEFAULT_BRANDING.colors.coral,
          softCoral: resolvedBranding.colors?.softCoral || DEFAULT_BRANDING.colors.softCoral,
          softMint: resolvedBranding.colors?.softMint || DEFAULT_BRANDING.colors.softMint,
          peach: resolvedBranding.colors?.peach || DEFAULT_BRANDING.colors.peach,
          lightTealGray: resolvedBranding.colors?.lightTealGray || DEFAULT_BRANDING.colors.lightTealGray,
          sageGray: resolvedBranding.colors?.sageGray || DEFAULT_BRANDING.colors.sageGray,
          warmGray: resolvedBranding.colors?.warmGray || DEFAULT_BRANDING.colors.warmGray,
          offWhite: resolvedBranding.colors?.offWhite || DEFAULT_BRANDING.colors.offWhite,
        },
        fontFamily: resolvedBranding.font?.family || DEFAULT_BRANDING.fontFamily,
        fontUrl: resolvedBranding.font?.url,
        headlineFontFamily: resolvedBranding.headlineFont?.family,
        headlineFontUrl: resolvedBranding.headlineFont?.url,
        assets: resolvedBranding.assets,
        isWhitelabelEnabled: resolvedBranding.isWhitelabelEnabled || false,
      };

      setBranding(effectiveBranding);
      injectCSSVariables(effectiveBranding);
    } catch (error) {
      devLog.warn('[ThemeProvider] Failed to refresh branding, using defaults:', error);
      setBranding(DEFAULT_BRANDING);
      injectCSSVariables(DEFAULT_BRANDING);
    }
  };

  const value: ThemeContextType = {
    branding,
    isLoading,
    refreshBranding,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
